/**
 * DrawingSaveManager manages pending save operations for drawings.
 * It tracks promises and allows aborting saves by drawingId.
 * Automatically cleans up when promises resolve, reject, or are aborted.
 */
class DrawingSaveManager {
  private pendingSaves: Map<number, Promise<any>>;

  constructor() {
    this.pendingSaves = new Map();
  }

  /**
   * Register a save operation for a drawing.
   * Sets up automatic cleanup when the promise settles.
   * @param drawingId - The ID of the drawing being saved
   * @param promise - The promise representing the save operation (must have an abort() method)
   */
  save(drawingId: number, promise: Promise<any>): void {
    if (this.pendingSaves.has(drawingId)) {
      const existingPromise = this.pendingSaves.get(drawingId);
      if (existingPromise && typeof (existingPromise as any).abort === 'function') {
        (existingPromise as any).abort();
      }
      this.pendingSaves.delete(drawingId);
    }
    this.pendingSaves.set(drawingId, promise);
    promise.finally(() => {
      this.pendingSaves.delete(drawingId);
    });
  }

  /**
   * Abort a pending save operation for a drawing.
   * @param drawingId - The ID of the drawing to abort saving
   * @returns true if a pending save was found and aborted, false otherwise
   */
  abort(drawingId: number): boolean {
    const promise = this.pendingSaves.get(drawingId);
    if (promise) {
      if (typeof (promise as any).abort === 'function') {
        (promise as any).abort();
      }
      this.pendingSaves.delete(drawingId);
      return true;
    }
    return false;
  }
}

export const drawingSaveManager = new DrawingSaveManager();
export default drawingSaveManager;
